/**
 * Plans / Pricing screen.
 *
 * Presents the escalating paid tiers alongside a monthly/annual cadence toggle,
 * mirroring the marketing site: each card shows a price, a feature list with
 * tick marks, the annual discount highlight, and a "Coming soon" state on the
 * two higher tiers (which are not yet purchasable).
 *
 * Display metadata — prices, taglines, feature lists, and the featured /
 * coming-soon flags — comes from the static {@link PLAN_CATALOG}, because the
 * runtime config's `stripePlans` field carries only non-secret price
 * identifiers, not dollar amounts or marketing copy. The catalog decides what
 * is shown; `getRuntimeConfig().stripePlans` decides what can actually check
 * out. A tier is purchasable only when it is BOTH in the catalog (not coming
 * soon) AND present in config; otherwise its CTA is disabled gracefully rather
 * than crashing.
 *
 * Confirming a purchasable tier initiates a Stripe Checkout session for the
 * selected (tier, cadence) via {@link createCheckoutSession} and redirects the
 * browser to the returned hosted-checkout `url`.
 *
 * All colours come from `var(--color-*)` design tokens (via inline styles and
 * the `.plans-*` / `.cadence-toggle*` / `.plan-*` classes in global.css) — zero
 * literal colour values (Req 12.1). The cadence toggle and tier CTAs carry
 * accessible names and are keyboard operable (Req 12.2, 12.3).
 *
 * _Requirements: 10.3, 10.4, 10.5, 12.1, 12.2, 12.3_
 * _Design: Components → 13 Frontend (Plans.tsx)_
 */

import { useEffect, useMemo, useState } from 'react';
import type { Tier, Cadence, PlanId } from '@covered-calls/shared';
import { createCheckoutSession } from '../services/payments-service';
import { redirectTo } from '../services/navigation';
import { getProfile } from '../services/profile-service';
import { getRuntimeConfig } from '../services/runtime-config';
import { PLAN_CATALOG, ANNUAL_DISCOUNT_NOTE, type PlanCatalogEntry } from './plan-catalog';

export function Plans() {
  const [cadence, setCadence] = useState<Cadence>('monthly');
  const [pendingTier, setPendingTier] = useState<Tier | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The user's current plan id, once the profile resolves. Kept null until (and
  // if) a plan block arrives; a null value means "no current plan" (trial
  // users, or any non-blocking fetch failure) — Req 10.5.
  const [currentPlanId, setCurrentPlanId] = useState<PlanId | null>(null);

  // The set of tiers that are actually configured for checkout in this env.
  // A tier must be BOTH in the static catalog AND have a config entry to be
  // purchasable; the visible price/features/labels always come from the catalog.
  const configuredTiers = useMemo(() => {
    const config = getRuntimeConfig();
    return new Set((config?.stripePlans ?? []).map((p) => p.tier));
  }, []);

  // Fetch the profile to learn the current plan so the matching tier can be
  // shown as the current plan (and its checkout suppressed). Non-blocking: a
  // failure or a response without a `plan` block leaves the default (no current
  // plan), mirroring today's behaviour. Guarded with an `active` flag so a late
  // resolution after unmount does not set state (cf. Trades.tsx).
  useEffect(() => {
    let active = true;
    getProfile()
      .then((profile) => {
        if (active && profile.plan) {
          setCurrentPlanId(profile.plan.planId);
        }
      })
      .catch(() => {
        // Non-blocking: keep the default (no current plan).
      });
    return () => {
      active = false;
    };
  }, []);

  async function handleChoose(tier: Tier): Promise<void> {
    setError(null);
    setPendingTier(tier);
    try {
      const { url } = await createCheckoutSession(tier, cadence);
      // The webhook is the source of truth for entitlements; the browser simply
      // follows Stripe's hosted checkout URL here (Req 10.4).
      redirectTo(url);
    } catch {
      setError('We could not start checkout. Please try again.');
      setPendingTier(null);
    }
  }

  const cadenceLabel = cadence === 'monthly' ? 'monthly' : 'annual';

  return (
    <section className="plans-screen" aria-labelledby="plans-heading">
      <h1 id="plans-heading" className="plans-title">
        Choose your plan
      </h1>
      <p className="plans-subtitle">
        Pick the plan that fits how you trade. Switch between monthly and annual billing at any time.
      </p>

      {/* Cadence toggle — a labelled radiogroup with two options. Keyboard
          operable via the underlying radio inputs; the selected option is
          exposed through the checked state, not colour alone (Req 12.2, 12.3). */}
      <fieldset
        className="cadence-toggle"
        role="radiogroup"
        aria-label="Billing cadence"
        style={{ border: 0, padding: '0.25rem', margin: '0 0 1.5rem' }}
      >
        <legend className="sr-only">Billing cadence</legend>
        {(['monthly', 'annual'] as Cadence[]).map((option) => {
          const selected = cadence === option;
          const label = option === 'monthly' ? 'Monthly' : 'Annual';
          return (
            <label
              key={option}
              className={`cadence-toggle-option${selected ? ' is-selected' : ''}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0.5rem 1rem',
                minHeight: '44px',
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name="billing-cadence"
                value={option}
                checked={selected}
                onChange={() => setCadence(option)}
                className="sr-only"
              />
              {label}
            </label>
          );
        })}
      </fieldset>

      {error && (
        <p role="alert" style={{ marginBottom: '1rem', color: 'var(--color-danger)', fontWeight: 500 }}>
          {error}
        </p>
      )}

      <div className="plans-grid">
        {PLAN_CATALOG.map((entry) => (
          <PlanCard
            key={entry.tier}
            entry={entry}
            cadence={cadence}
            cadenceLabel={cadenceLabel}
            purchasable={configuredTiers.has(entry.tier)}
            isCurrent={currentPlanId === entry.tier}
            busy={pendingTier === entry.tier}
            onChoose={handleChoose}
          />
        ))}
      </div>
    </section>
  );
}

interface PlanCardProps {
  entry: PlanCatalogEntry;
  cadence: Cadence;
  cadenceLabel: string;
  /** True when this tier is configured in `stripePlans` (can resolve a price). */
  purchasable: boolean;
  /** True when this tier matches the user's current plan. */
  isCurrent: boolean;
  /** True while a checkout redirect for this tier is in flight. */
  busy: boolean;
  onChoose: (tier: Tier) => void;
}

/** A single tier card — price, features, discount highlight, and CTA. */
function PlanCard({
  entry,
  cadence,
  cadenceLabel,
  purchasable,
  isCurrent,
  busy,
  onChoose,
}: PlanCardProps) {
  const { tier, displayName, tagline, comingSoon, featured } = entry;
  const price = cadence === 'monthly' ? entry.monthlyPrice : entry.annualPrice;
  const priceSuffix = cadence === 'monthly' ? '/month' : '/year';

  const cardClass =
    'plan-card' +
    (featured ? ' plan-card--featured' : '') +
    (comingSoon ? ' plan-card--coming-soon' : '');

  // CTA label + accessible name + behaviour depend on the tier's state.
  // Priority: current plan → coming soon → unavailable (no config) → choose.
  let ctaLabel: string;
  let ctaAccessibleName: string;
  let ctaDisabled: boolean;

  if (isCurrent) {
    ctaLabel = 'Current plan';
    ctaAccessibleName = `${displayName} is your current plan`;
    ctaDisabled = true;
  } else if (comingSoon) {
    ctaLabel = 'Coming soon';
    ctaAccessibleName = `${displayName} — coming soon`;
    ctaDisabled = true;
  } else if (!purchasable) {
    ctaLabel = 'Unavailable';
    ctaAccessibleName = `${displayName} is currently unavailable`;
    ctaDisabled = true;
  } else {
    ctaLabel = busy ? 'Redirecting…' : `Choose ${displayName}`;
    ctaAccessibleName = `Choose ${displayName}, billed ${cadenceLabel}`;
    ctaDisabled = busy;
  }

  return (
    <div className={cardClass}>
      <div className="plan-card-badges">
        {featured && (
          <p className="plan-card-badge plan-card-badge--featured">
            Most popular
          </p>
        )}
        {comingSoon && (
          <p className="plan-card-badge plan-card-badge--coming-soon">
            Coming soon
          </p>
        )}
        {isCurrent && (
          <p className="plan-card-badge plan-card-badge--current">
            Current plan
          </p>
        )}
      </div>

      <h2 className="plan-card-name">{displayName}</h2>
      <p className="plan-card-tagline">{tagline}</p>

      <p className="plan-card-price">
        {price}
        <span className="plan-card-price-suffix">{priceSuffix}</span>
      </p>
      <p
        className={
          cadence === 'annual' ? 'plan-card-cadence plan-card-cadence--discount' : 'plan-card-cadence'
        }
      >
        {cadence === 'annual' ? ANNUAL_DISCOUNT_NOTE : 'Billed monthly'}
      </p>

      <ul className="plan-card-features">
        {entry.features.map((feature) => (
          <li key={feature} className="plan-card-feature">
            <span className="plan-card-feature-tick" aria-hidden="true">
              ✓
            </span>
            {feature}
          </li>
        ))}
      </ul>

      <button
        type="button"
        className="plan-cta"
        style={{ padding: '0.75rem 1rem', minHeight: '44px', fontSize: '1rem' }}
        disabled={ctaDisabled}
        aria-label={ctaAccessibleName}
        onClick={() => {
          if (ctaDisabled) {
            return;
          }
          onChoose(tier);
        }}
      >
        {ctaLabel}
      </button>
    </div>
  );
}
